/**
 * Closes the open header panel when the page scrolls.
 *
 * The panel is a modal over the page, and its content is short enough that there is nothing to
 * scroll inside it — so a scroll gesture is the reader asking for the page, not for the menu.
 * Clicking outside already dismisses it; this makes scrolling do the same -- but only while the
 * pointer is outside the panel. Scrolling with the pointer over it is someone reading it.
 *
 * It closes by clicking the trigger rather than by writing `aria-expanded` directly, so the close
 * runs through `header-menu`'s own `toggle` — same teardown, same state, nothing duplicated here
 * that could drift from it. Dispatching Escape would also work, but `header-menu` restores focus to
 * the trigger afterwards, and pulling focus back to the header mid-scroll would fight the very
 * gesture that triggered the close.
 *
 * A plain listener rather than a custom element, matching `assets/auto-close-details.js`, which is
 * this theme's existing answer to the same shape of problem: a global dismiss with no UI of its own.
 */
(function brwdMenuScrollClose() {
  const TRIGGER = '.menu-list__link--brwd-panel[aria-expanded="true"]';

  function closeOpenPanel() {
    const trigger = document.querySelector(TRIGGER);
    if (!(trigger instanceof HTMLElement)) return;

    // A reader whose pointer is inside the panel is reading the panel, and a scroll there is not a
    // request to be rid of it. The list item is the right thing to test because the panel is its
    // child, so one `:hover` covers both the tab and the panel it opened.
    if (trigger.closest('.menu-list__list-item')?.matches(':hover')) return;

    trigger.dispatchEvent(new PointerEvent('click', { bubbles: true, cancelable: true }));
  }

  // Deliberately unthrottled. An earlier version gated this behind a flag cleared inside
  // `requestAnimationFrame`, which latched on permanently the moment a frame did not run -- a
  // hidden tab is enough -- and silently killed the feature. The work saved was one
  // `querySelector` that matches nothing on all but a handful of page views.
  //
  // `wheel` and `touchmove` are the gesture; `scroll` is only its consequence. The panel is short
  // enough that a wheel over it often scrolls nothing at all, and then no `scroll` event is ever
  // dispatched -- measured: the panel stayed open through a wheel that fired zero scroll events.
  // Listening to the gesture closes it whether or not anything moved.
  //
  // `scroll` stays for the cases the gesture events miss: keyboard paging, and a programmatic
  // scroll. It is captured rather than bound to window because this theme scrolls inside
  // `.page-wrapper` -- it carries `overflow-x: auto`, which makes it the scroll container -- and a
  // scroll event from an element does not bubble.
  window.addEventListener('wheel', closeOpenPanel, { passive: true });
  window.addEventListener('touchmove', closeOpenPanel, { passive: true });
  document.addEventListener('scroll', closeOpenPanel, { capture: true, passive: true });
})();
